import { setUsername } from "../redux/actions/user";
import { store } from "../redux/store";
import { getStoreReceipt } from "./deviceReceiptService";
import { lookupUsernameByReceipt } from "./authService";

// Attempts to pre-populate `state.user.usernameOrEmail` from a device store
// receipt when the app starts in an unauthenticated state with no saved
// username. The downstream selector (AuthStackScreen) routes to the Login
// screen — pre-filled — when this value is set, instead of the
// RegisterOrLogin screen. Silent on every failure: the worst-case outcome
// is the user simply seeing the normal RegisterOrLogin entry point.
//
// Imports the singleton store directly (same pattern as the other
// dispatch-from-service files like integrationService, AdMobService).
// Called from Root in route/index.tsx via a useRef-gated useEffect so
// it only fires on cold-start, not on in-session logout transitions.
export const prePopulateUsernameFromReceipt = async (): Promise<void> => {
    try {
        const state = store.getState();
        const existing = state.user && state.user.usernameOrEmail;
        if (existing) {
            // User already has a saved login on this device — nothing to do.
            return;
        }

        const receiptPayload = await getStoreReceipt();
        if (!receiptPayload) {
            return;
        }

        const username = await lookupUsernameByReceipt(receiptPayload);
        if (!username) {
            return;
        }

        // Re-check post-await to avoid clobbering a username the user
        // typed/imported while the network call was in flight.
        const stateNow = store.getState();
        if (stateNow.user && stateNow.user.usernameOrEmail) {
            return;
        }

        store.dispatch(setUsername(username));
    } catch (err) {
        // Silent — never surface to the user — but emit a warning so a
        // future regression in this orchestration leaves a breadcrumb in
        // the console / Sentry instead of disabling username pre-fill
        // with no signal.
        console.warn("prePopulateUsernameFromReceipt failed", err);
    }
};
